import * as PopoverPrimitive from '@radix-ui/react-popover';
import { CalendarRange, ChevronDown, X } from 'lucide-react';
import * as React from 'react';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import {
  intervaloDoPreset,
  presetAtivo,
  PRESETS_PERIODO,
  rotuloPeriodo,
  type Periodo,
  type PresetPeriodo,
} from '@/lib/periodo';
import { cn } from '@/lib/utils';
import { CampoData } from './campo-data';

/**
 * Filtro de período num popover só (o mesmo padrão do PAN: intervalo personalizado à esquerda, com `CampoData`
 * dd/mm/aaaa digitável + calendário, e atalhos à direita), em vez de campos "De"/"Até" soltos na barra de filtros.
 * Atalho vale na hora e fecha; o intervalo personalizado só vale ao "Aplicar intervalo". Os campos já impedem um
 * intervalo invertido (De nunca depois de Até). Datas em dia civil de São Paulo (`lib/periodo.ts`).
 */
export function FiltroPeriodo({
  id,
  de,
  ate,
  aoMudar,
  className,
}: {
  id: string;
  de: string | null;
  ate: string | null;
  aoMudar: (periodo: Periodo) => void;
  className?: string;
}) {
  const [aberto, setAberto] = React.useState(false);
  const [rascunhoDe, setRascunhoDe] = React.useState(de ?? '');
  const [rascunhoAte, setRascunhoAte] = React.useState(ate ?? '');
  const preset = presetAtivo(de, ate);
  const semPeriodo = !de && !ate;

  function alternar(valor: boolean) {
    setAberto(valor);
    // O rascunho só reinicia ao ABRIR: fechar sem aplicar não perde o que a pessoa digitou se reabrir em seguida.
    if (valor) {
      setRascunhoDe(de ?? '');
      setRascunhoAte(ate ?? '');
    }
  }

  function escolherPreset(valor: PresetPeriodo | null) {
    aoMudar(valor ? intervaloDoPreset(valor) : { de: null, ate: null });
    setAberto(false);
  }

  function aplicar() {
    // Defesa a mais: os campos já bloqueiam o intervalo invertido, mas dois campos digitados rápido, sem sair de um
    // antes de mexer no outro, ainda podem chegar aqui invertidos.
    const deFinal = rascunhoDe && rascunhoAte && rascunhoDe > rascunhoAte ? rascunhoAte : rascunhoDe;
    aoMudar({ de: deFinal || null, ate: rascunhoAte || null });
    setAberto(false);
  }

  const opcao = (ativa: boolean) =>
    cn(
      'rounded-chip px-2.5 py-2 text-left text-body transition-colors duration-fast ease-standard hover:bg-bg-subtle',
      ativa && 'bg-primary-wash font-bold text-primary',
    );

  return (
    <Field id={id} label="Período" className={cn('min-w-48', className)}>
      {(props) => (
        <PopoverPrimitive.Root open={aberto} onOpenChange={alternar}>
          <PopoverPrimitive.Trigger asChild>
            <button
              id={props.id}
              type="button"
              aria-describedby={props['aria-describedby']}
              className="flex h-10 w-full items-center gap-2 rounded-input border border-border-strong bg-bg-surface px-3 text-body text-on-surface transition-colors duration-fast ease-standard hover:border-primary"
            >
              <CalendarRange className="size-4 shrink-0 text-icon" aria-hidden="true" />
              <span className="flex-1 truncate text-left">{rotuloPeriodo(de, ate)}</span>
              <ChevronDown className="size-4 shrink-0 text-icon" aria-hidden="true" />
            </button>
          </PopoverPrimitive.Trigger>

          <PopoverPrimitive.Portal>
            <PopoverPrimitive.Content
              align="start"
              sideOffset={4}
              collisionPadding={8}
              // z-[70]: acima de diálogos e folhas (o popover renderiza num portal próprio).
              className="z-[70] flex max-h-[var(--radix-popover-content-available-height)] max-w-[calc(100vw-1rem)] flex-col-reverse overflow-y-auto rounded-input border border-border bg-bg-surface shadow-md duration-base ease-standard data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 sm:flex-row sm:overflow-hidden"
            >
              <div className="flex w-full flex-col gap-3 border-t border-border p-3 sm:w-52 sm:border-r sm:border-t-0">
                <span className="text-micro font-bold uppercase tracking-[0.06em] text-on-surface-muted">
                  Intervalo personalizado
                </span>
                <Field id={`${id}-de`} label="De">
                  {(campo) => (
                    <CampoData {...campo} valor={rascunhoDe} aoMudar={setRascunhoDe} max={rascunhoAte || undefined} />
                  )}
                </Field>
                <Field id={`${id}-ate`} label="Até">
                  {(campo) => (
                    <CampoData {...campo} valor={rascunhoAte} aoMudar={setRascunhoAte} min={rascunhoDe || undefined} />
                  )}
                </Field>
                <Button size="sm" variant="primary" disabled={!rascunhoDe && !rascunhoAte} onClick={aplicar}>
                  Aplicar intervalo
                </Button>
              </div>

              <div className="grid w-full grid-cols-2 gap-0.5 p-2 sm:flex sm:w-40 sm:flex-col">
                <button type="button" onClick={() => escolherPreset(null)} className={opcao(semPeriodo)}>
                  Qualquer período
                </button>
                {PRESETS_PERIODO.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => escolherPreset(item.id)}
                    className={opcao(preset === item.id)}
                  >
                    {item.rotulo}
                  </button>
                ))}
              </div>
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>
      )}
    </Field>
  );
}

/** Indicador do período ativo, com botão de limpar. Não renderiza sem período. */
export function ChipPeriodo({
  de,
  ate,
  aoLimpar,
  className,
}: {
  de: string | null;
  ate: string | null;
  aoLimpar: () => void;
  className?: string;
}) {
  if (!de && !ate) return null;
  const preset = presetAtivo(de, ate);
  const rotulo = rotuloPeriodo(de, ate);
  const detalhe =
    preset && de && ate
      ? de === ate
        ? null
        : `${de.split('-').reverse().join('/')} a ${ate.split('-').reverse().join('/')}`
      : null;
  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center gap-1.5 rounded-chip border border-primary-border bg-primary-wash py-0.5 pl-2 pr-0.5 text-label text-primary',
        className,
      )}
    >
      <CalendarRange className="size-3.5 shrink-0" aria-hidden="true" />
      <span className="truncate">
        Período: <strong>{rotulo}</strong>
        {detalhe ? <span className="font-normal text-on-surface-muted"> ({detalhe})</span> : null}
      </span>
      <button
        type="button"
        onClick={aoLimpar}
        aria-label="Limpar período"
        className="inline-flex size-6 shrink-0 items-center justify-center rounded-full hover:bg-bg-surface"
      >
        <X className="size-3.5" aria-hidden="true" />
      </button>
    </span>
  );
}
